import { useState, useEffect, useCallback } from 'react';
import AdminNav from '../components/AdminNav';
import { Plus, Trash2, Edit2, Copy, Check, X, Eye, EyeOff, Share2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatExpiry(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function isExpired(iso) {
  if (!iso) return false;
  return new Date(iso) < new Date();
}

export default function ShowroomGuestsPage() {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', login: '', password: '', email: '', expires_at: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(null);
  const [showPw, setShowPw] = useState(false);

  const fetchGuests = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/showroom-guests`, { credentials: 'include' });
      const data = await res.json();
      setGuests(data.items || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchGuests(); }, [fetchGuests]);

  const openCreate = () => {
    setEditId(null);
    setForm({ name: '', login: '', password: '', email: '', expires_at: '' });
    setShowForm(true);
    setError('');
    setShowPw(false);
  };

  const openEdit = (g) => {
    setEditId(g.id);
    const dt = g.expires_at ? new Date(g.expires_at) : null;
    const localIso = dt ? new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : '';
    setForm({ name: g.name, login: g.login || '', password: '', email: g.email || '', expires_at: localIso });
    setShowForm(true);
    setError('');
    setShowPw(false);
  };

  const handleSave = async () => {
    setError('');
    if (!form.name.trim() || !form.login.trim()) { setError('Name and login required'); return; }
    if (!editId && !form.password) { setError('Password required'); return; }
    if (!form.expires_at) { setError('Expiration date required'); return; }
    setSaving(true);

    const expiresUtc = new Date(form.expires_at).toISOString();
    const body = { ...form, expires_at: expiresUtc };
    if (editId && !body.password) delete body.password;

    try {
      const url = editId ? `${API_URL}/api/showroom-guests/${editId}` : `${API_URL}/api/showroom-guests`;
      const res = await fetch(url, {
        method: editId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Save failed'); setSaving(false); return; }
      setShowForm(false);
      fetchGuests();
    } catch { setError('Connection error'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this guest access?')) return;
    await fetch(`${API_URL}/api/showroom-guests/${id}`, { method: 'DELETE', credentials: 'include' });
    fetchGuests();
  };

  const toggleActive = async (g) => {
    await fetch(`${API_URL}/api/showroom-guests/${g.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ active: !g.active }),
    });
    fetchGuests();
  };

  const copyText = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };

  const resetAndCopy = async (g) => {
    if (!window.confirm(`This will reset ${g.name}'s password and copy the new credentials to your clipboard. Continue?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/showroom-guests/${g.id}/reset-password`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) { alert('Failed to reset password'); return; }
      const data = await res.json();
      const loginUrl = `${window.location.origin}/showroom/login`;
      const block = `Showroom: ${loginUrl}\nLogin: ${data.login}\nPassword: ${data.password}`;
      await navigator.clipboard.writeText(block);
      setCopied(`creds-${g.id}`);
      setTimeout(() => setCopied(null), 2500);
    } catch { alert('Connection error'); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="showroom-guests-page">
      <AdminNav />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-lg font-semibold text-slate-800" data-testid="guests-page-title">Showroom Guests</h1>
            <p className="text-xs text-slate-500 mt-0.5">Temporary full-showroom credentials with expiration</p>
          </div>
          <button
            onClick={openCreate}
            data-testid="create-guest-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#0077FF] hover:bg-blue-600 text-white text-xs font-medium rounded-lg transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> New Guest
          </button>
        </div>

        {showForm && (
          <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6 shadow-sm" data-testid="guest-form">
            <h2 className="text-sm font-semibold text-slate-700 mb-4">{editId ? 'Edit Guest' : 'Create Guest Access'}</h2>
            {error && <div className="mb-3 text-xs text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="guest-form-error">{error}</div>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Name *</label>
                <input
                  data-testid="guest-form-name"
                  value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="John Smith"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Login *</label>
                <input
                  data-testid="guest-form-login"
                  value={form.login} onChange={e => setForm({ ...form, login: e.target.value })}
                  placeholder="Marriott2026"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Password {editId ? '(leave blank to keep)' : '*'}</label>
                <div className="relative">
                  <input
                    data-testid="guest-form-password"
                    type={showPw ? 'text' : 'password'}
                    value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                    placeholder={editId ? '••••••' : 'Choose a password'}
                    className="w-full px-3 py-2 pr-9 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600">
                    {showPw ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Email (optional)</label>
                <input
                  data-testid="guest-form-email"
                  type="email"
                  value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="john@example.com"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">Expires At *</label>
                <input
                  data-testid="guest-form-expires"
                  type="datetime-local"
                  value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSave} disabled={saving}
                data-testid="guest-form-save"
                className="px-4 py-2 bg-[#0077FF] hover:bg-blue-600 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving…' : editId ? 'Update' : 'Create'}
              </button>
              <button
                onClick={() => setShowForm(false)}
                data-testid="guest-form-cancel"
                className="px-4 py-2 text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 text-xs font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-slate-400 text-sm">Loading…</div>
        ) : guests.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
            <p className="text-slate-400 text-sm">No guest access created yet</p>
            <p className="text-slate-400 text-xs mt-1">Click "New Guest" to create temporary showroom credentials</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm" data-testid="guests-table">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Name</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Login</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Expires</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Status</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {guests.map(g => {
                  const expired = isExpired(g.expires_at);
                  const active = g.active && !expired;
                  return (
                    <tr key={g.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50" data-testid={`guest-row-${g.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-700">{g.name}</div>
                        {g.email && <div className="text-xs text-slate-400">{g.email}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <code className="text-xs bg-slate-100 px-2 py-0.5 rounded font-mono text-slate-600">{g.login}</code>
                          <button
                            onClick={() => copyText(g.login, `login-${g.id}`)}
                            className="text-slate-300 hover:text-slate-500 transition-colors"
                            title="Copy login"
                          >
                            {copied === `login-${g.id}` ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        <span className={expired ? 'text-red-400' : ''}>{formatExpiry(g.expires_at)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggleActive(g)}
                          data-testid={`guest-toggle-${g.id}`}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
                            active
                              ? 'bg-green-50 text-green-600 hover:bg-green-100'
                              : expired
                              ? 'bg-red-50 text-red-400'
                              : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                          }`}
                        >
                          {expired ? 'Expired' : active ? 'Active' : 'Revoked'}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {active && (
                            <button
                              onClick={() => resetAndCopy(g)}
                              data-testid={`guest-copy-creds-${g.id}`}
                              className={`inline-flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                                copied === `creds-${g.id}`
                                  ? 'bg-green-50 text-green-600'
                                  : 'text-slate-500 hover:text-blue-600 hover:bg-blue-50'
                              }`}
                              title="Reset password & copy credentials"
                            >
                              {copied === `creds-${g.id}` ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
                              {copied === `creds-${g.id}` ? 'Copied' : 'Copy Credentials'}
                            </button>
                          )}
                          <button onClick={() => openEdit(g)} className="p-1.5 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors" title="Edit" data-testid={`guest-edit-${g.id}`}>
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => handleDelete(g.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Delete" data-testid={`guest-delete-${g.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
